/**
 * Shared diff-rendering primitives for "what's about to change" UIs.
 *
 * Three exports:
 *   <ValueDisplay value={...} />                 — renders a single value
 *   <ValueDiff oldValue={...} newValue={...} /> — per-item diff between two
 *   <DiffLine kind="added|removed|unchanged|plain"> ...  — single row helper
 *
 * Arrays of primitives get a per-item diff (only added/removed shown).
 * Strings, numbers, booleans render as a plain row.
 * Objects render as pretty-printed JSON.
 */
import React from 'react';

const T = {
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  ink: '#2E2109',
  ink2: '#4A3510',
  inkMuted: '#6E5A38',
  inkFaint: '#A39068',
  capRed: '#C14A33',
};

function tryParseJSON(v) {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return v;
  try {
    return JSON.parse(trimmed);
  } catch {
    return v;
  }
}

function itemKey(x) {
  if (x == null) return 'null';
  if (typeof x === 'string') return `s:${x}`;
  if (typeof x === 'number' || typeof x === 'boolean') return `p:${x}`;
  try {
    return `j:${JSON.stringify(x)}`;
  } catch {
    return `o:${String(x)}`;
  }
}

function renderItemBody(item) {
  if (item == null) return <span style={{ color: T.inkMuted }}>—</span>;
  if (typeof item === 'string') return item;
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);
  return (
    <pre style={{
      margin: 0, fontFamily: T.mono, fontSize: 12, color: T.ink2,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>
      {JSON.stringify(item, null, 2)}
    </pre>
  );
}

const KIND_STYLES = {
  added: {
    background: 'rgba(140,158,86,0.20)',
    border: '1px solid rgba(107,122,62,0.35)',
    color: T.ink,
    prefix: '+',
    prefixColor: '#506024',
  },
  removed: {
    background: 'rgba(193,74,51,0.10)',
    border: '1px solid rgba(193,74,51,0.30)',
    color: T.inkMuted,
    prefix: '−',
    prefixColor: T.capRed,
    strike: true,
  },
  unchanged: {
    background: 'rgba(214,196,154,0.18)',
    border: '1px dashed rgba(191,171,121,0.45)',
    color: T.inkMuted,
    prefix: ' ',
    prefixColor: T.inkFaint,
  },
  plain: {
    background: 'rgba(255,251,236,0.7)',
    border: '1px solid rgba(191,171,121,0.32)',
    color: T.ink2,
    prefix: '',
    prefixColor: T.inkFaint,
  },
};

export function DiffLine({ kind, children }) {
  const s = KIND_STYLES[kind] || KIND_STYLES.plain;
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start',
      padding: '6px 10px', marginBottom: 4, borderRadius: 6,
      background: s.background, border: s.border,
      fontFamily: T.sans, fontSize: 13, lineHeight: 1.5,
      color: s.color,
      textDecoration: s.strike ? 'line-through' : 'none',
    }}>
      {s.prefix && (
        <span style={{
          fontFamily: T.mono, fontWeight: 800, color: s.prefixColor,
          flexShrink: 0, minWidth: 12, textAlign: 'center',
        }}>
          {s.prefix}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
        {children}
      </span>
    </div>
  );
}

function arrayDiff(oldArr, newArr) {
  const oldKeys = oldArr.map(itemKey);
  const newKeys = newArr.map(itemKey);
  const newSet = new Set(newKeys);
  const oldSet = new Set(oldKeys);
  const removed = oldArr.filter((_, i) => !newSet.has(oldKeys[i]));
  const added = newArr.filter((_, i) => !oldSet.has(newKeys[i]));
  const unchangedCount = oldKeys.filter((k) => newSet.has(k)).length;
  return { removed, added, unchangedCount };
}

export function ValueDisplay({ value }) {
  const v = tryParseJSON(value);

  if (v == null) {
    return <DiffLine kind="plain"><em style={{ color: T.inkMuted }}>(empty)</em></DiffLine>;
  }
  if (Array.isArray(v)) {
    if (v.length === 0) {
      return <DiffLine kind="plain"><em style={{ color: T.inkMuted }}>(empty list)</em></DiffLine>;
    }
    return (
      <div>
        {v.map((item, i) => (
          <DiffLine kind="plain" key={i}>{renderItemBody(item)}</DiffLine>
        ))}
      </div>
    );
  }
  if (typeof v === 'object') {
    return (
      <DiffLine kind="plain">
        <pre style={{
          margin: 0, fontFamily: T.mono, fontSize: 12,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {JSON.stringify(v, null, 2)}
        </pre>
      </DiffLine>
    );
  }
  return <DiffLine kind="plain">{String(v)}</DiffLine>;
}

export function ValueDiff({ oldValue, newValue }) {
  const oldV = tryParseJSON(oldValue);
  const newV = tryParseJSON(newValue);

  // Both arrays — per-item diff
  if (Array.isArray(oldV) && Array.isArray(newV)) {
    const { removed, added, unchangedCount } = arrayDiff(oldV, newV);

    if (removed.length === 0 && added.length === 0) {
      return (
        <DiffLine kind="unchanged">
          <em>List unchanged ({unchangedCount} item{unchangedCount === 1 ? '' : 's'})</em>
        </DiffLine>
      );
    }

    return (
      <div>
        {removed.map((item, i) => (
          <DiffLine kind="removed" key={`r-${i}`}>{renderItemBody(item)}</DiffLine>
        ))}
        {added.map((item, i) => (
          <DiffLine kind="added" key={`a-${i}`}>{renderItemBody(item)}</DiffLine>
        ))}
        {unchangedCount > 0 && (
          <div style={{
            fontFamily: T.sans, fontSize: 11, color: T.inkMuted,
            marginTop: 2, fontStyle: 'italic',
          }}>
            {unchangedCount} item{unchangedCount === 1 ? '' : 's'} unchanged
          </div>
        )}
      </div>
    );
  }

  // Either is null/undefined → render the side that exists
  if (oldV == null && newV != null) {
    return <DiffLine kind="added">{renderItemBody(newV)}</DiffLine>;
  }
  if (newV == null && oldV != null) {
    return <DiffLine kind="removed">{renderItemBody(oldV)}</DiffLine>;
  }

  // Both scalars/objects — stack old over new
  return (
    <div>
      {oldV != null && <DiffLine kind="removed">{renderItemBody(oldV)}</DiffLine>}
      {newV != null && <DiffLine kind="added">{renderItemBody(newV)}</DiffLine>}
    </div>
  );
}
